import React from "react";
import { DropdownMenu as RadixDropdownMenu } from "radix-ui";
import { cn } from "@/utils/cn";

export type DropdownMenuItem = {
  label: string;
  onSelect: () => void;
  /** Leading icon rendered before the label. */
  icon?: React.ReactNode;
  /** Extra classes merged onto this item, e.g. to override the default label styling. */
  itemClassName?: string;
  /** "danger" styles the item red — for destructive actions like Sign out. */
  variant?: "default" | "danger";
};

export type DropdownMenuProps = {
  trigger: React.ReactElement;
  items: DropdownMenuItem[];
  /**
   * Non-interactive content rendered above the item list (e.g. an identity
   * block) — visually separated from items by a divider. Lets a menu carry a
   * header without every consumer having to build the divider/spacing itself.
   */
  header?: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  /** Extra classes merged onto the menu panel, e.g. to override the default shadow. */
  contentClassName?: string;
};

function DropdownMenu({
  trigger,
  items,
  header,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  contentClassName,
}: DropdownMenuProps) {
  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger asChild>{trigger}</RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            "z-radixPopover min-w-[160px] overflow-hidden rounded-md border border-border-default bg-white shadow-dialog",
            contentClassName
          )}
        >
          {header && (
            <>
              <div className="px-4 py-3">{header}</div>
              <RadixDropdownMenu.Separator className="h-px bg-border-light" />
            </>
          )}
          <div className="py-1">
            {items.map((item) => (
              <RadixDropdownMenu.Item
                key={item.label}
                onSelect={item.onSelect}
                className={cn(
                  "flex cursor-pointer items-center gap-2 px-4 py-2 text-sm outline-none",
                  item.variant === "danger"
                    ? "text-status-error-text hover:bg-status-error-bg"
                    : "text-text-primary hover:bg-surface-muted",
                  item.itemClassName
                )}
              >
                {item.icon}
                {item.label}
              </RadixDropdownMenu.Item>
            ))}
          </div>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}

export default DropdownMenu;
